import type { AppLanguage } from '../i18n';
import {
  assetRecordDisplayTitle,
  assetRecordListAddressLine,
  assetRecordListTitle,
} from '../components/AssetGetByIdView';
import { BROWSE_FILTER_ASSET_TYPE_NAMES, browseSpokenPhrasesForType } from '../constants/filterableAssetTypes';
import { allIsraelSettlements } from './israelSettlements';
import {
  matchVoiceSearchCity,
  removeVoicePhrase,
  stripHebrewPrefixLetters,
} from '../assistant/matchVoiceSearchCity';
import { assetRecordMatchesBrowseCity } from './assetSearchRecordFilters';
import { facilityAddonIconId } from './assetFacilityIcons';
import {
  catalogRowDisplayName,
  mergeFacilitiesAndPromotedAddonsForDisplay,
} from './assetPromotedFacilities';

/** Extra HE/EN tokens so amenity listSearch (“מטבח”) matches English facility labels. */
const FACILITY_ICON_SEARCH_ALIASES: Partial<Record<string, string[]>> = {
  kitchen: ['מטבח', 'kitchen', 'kitchenette', 'מטבחון'],
  wifi: ['wifi', 'wi-fi', 'אינטרנט', 'ווייפיי'],
  parking: ['חניה', 'parking', 'פרקינג'],
};

/** Minimum query length before keyword search runs (API + client refinement). */
export const ASSET_KEYWORD_SEARCH_MIN_LENGTH = 3;

export type AssetKeywordMatchKind = 'none' | 'fuzzy' | 'partial' | 'titlePartial' | 'titlePrefix';

export type AssetKeywordMatchScore = {
  matches: boolean;
  kind: AssetKeywordMatchKind;
  /** Higher sorts earlier in result lists. */
  rank: number;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function normalizeAssetKeywordQuery(query: string): string {
  return query
    .trim()
    .toLowerCase()
    .replace(/קריית/g, 'קרית')
    .replace(/[־–—-]+/g, ' ')
    .replace(/[''`´]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isAssetKeywordQueryActive(query: string): boolean {
  return normalizeAssetKeywordQuery(query).length >= ASSET_KEYWORD_SEARCH_MIN_LENGTH;
}

function levenshtein(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  if (!a.length) {
    return b.length;
  }
  if (!b.length) {
    return a.length;
  }
  const rows = a.length + 1;
  const cols = b.length + 1;
  const matrix: number[] = new Array(rows * cols);
  for (let i = 0; i < rows; i += 1) {
    matrix[i * cols] = i;
  }
  for (let j = 0; j < cols; j += 1) {
    matrix[j] = j;
  }
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const idx = i * cols + j;
      matrix[idx] = Math.min(
        matrix[(i - 1) * cols + j] + 1,
        matrix[i * cols + (j - 1)] + 1,
        matrix[(i - 1) * cols + (j - 1)] + cost,
      );
    }
  }
  return matrix[(rows - 1) * cols + (cols - 1)];
}

const QUERY_STOP_WORDS = new Set([
  'in',
  'at',
  'the',
  'of',
  'for',
  'near',
  'and',
  'to',
  'של',
  'את',
  'על',
  'עם',
  'ליד',
  'כמה',
  'how',
  'many',
  'נכס',
  'נכסים',
  'פארק',
  'התעשיות',
  'התעשיה',
  'תעשיות',
  'תעשייה',
  'תעשיה',
  'אזור',
  'industrial',
  'רחוב',
  'ברחוב',
  'street',
  'st',
  'asset',
  'assets',
  'property',
  'properties',
  'listing',
  'listings',
  'place',
  'places',
]);

function tokenizeSearchText(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9\u0590-\u05ff]+/i)
    .map((token) => stripHebrewKeywordPrefix(token.trim()))
    .filter((token) => token.length >= 2 || /^[a-z]$/i.test(token));
}

/**
 * “בתל אביב” keeps בתל (תל is only two letters, so the usual ב/ל/מ strip
 * does not run). If the next token completes a known city, drop the prefix.
 */
function stripHebrewCityPrefixTokens(tokens: string[]): string[] {
  const bigrams = cityTokenBigrams();
  return tokens.map((token, index) => {
    if (!/^[בלמ][\u0590-\u05ff]{2,}$/.test(token)) {
      return token;
    }
    const stem = token.slice(1);
    const next = tokens[index + 1];
    if (next && bigrams.has(`${stem} ${next}`)) {
      return stem;
    }
    return token;
  });
}

/** ב/ל/מ on a long token (“בפארק”) — not two-letter names like “בר”. */
function stripHebrewKeywordPrefix(token: string): string {
  if (/^[בלמ][\u0590-\u05ff]{3,}/.test(token)) {
    return token.slice(1);
  }
  return token;
}

function isLatinToken(value: string): boolean {
  return /^[a-z0-9]+$/i.test(value);
}

function isHebrewToken(value: string): boolean {
  return /[\u0590-\u05ff]/.test(value);
}

/** Spoken Hebrew names of Latin letters used in brand names (“איקס” → X). */
const HEBREW_LATIN_LETTER_NAMES: Record<string, string[]> = {
  איקס: ['x', 'ix', 'ex', 'iks'],
  אקס: ['x', 'ex', 'aks'],
};

const HEBREW_TO_LATIN: Record<string, string> = {
  א: 'a',
  ב: 'b',
  ג: 'g',
  ד: 'd',
  ה: 'h',
  ו: 'o',
  ז: 'z',
  ח: 'h',
  ט: 't',
  י: 'y',
  כ: 'k',
  ך: 'k',
  ל: 'l',
  מ: 'm',
  ם: 'm',
  נ: 'n',
  ן: 'n',
  ס: 's',
  ע: 'a',
  פ: 'p',
  ף: 'p',
  צ: 'z',
  ץ: 'z',
  ק: 'k',
  ר: 'r',
  ש: 'sh',
  ת: 't',
};

function latinizeHebrewToken(token: string): string {
  let out = '';
  for (const ch of token) {
    out += HEBREW_TO_LATIN[ch] ?? (/[a-z0-9]/i.test(ch) ? ch.toLowerCase() : '');
  }
  return out;
}

function consonantSkeleton(value: string): string {
  const latin = isHebrewToken(value)
    ? latinizeHebrewToken(value)
    : normalizeAssetKeywordQuery(value).replace(/[^a-z0-9]/g, '');
  // Collapse doubles so “rabbit” (rbbt) matches “ראביט” (rbt).
  return latin.replace(/[aeiouhwy]/g, '').replace(/(.)\1+/g, '$1');
}

/** Single-consonant leftovers after vowel strip (“hole”→l) — too weak alone. */
function isWeakSkeletonToken(token: string): boolean {
  return consonantSkeleton(token).length <= 1;
}

function hebrewLetterNameLatin(token: string): string[] {
  return HEBREW_LATIN_LETTER_NAMES[normalizeAssetKeywordQuery(token)] ?? [];
}

function skeletonsMatch(a: string, b: string): boolean {
  const sa = consonantSkeleton(a);
  const sb = consonantSkeleton(b);
  if (!sa || !sb) {
    return false;
  }
  if (sa !== sb) {
    return false;
  }
  if (sa.length >= 3) {
    return true;
  }
  // Two consonants are only enough for short cross-script words (tel ↔ תל);
  // "holon" → "ln" would otherwise match אלון, חלון, אילן.
  return (
    sa.length === 2 &&
    isHebrewToken(a) !== isHebrewToken(b) &&
    a.length <= 3 &&
    b.length <= 3
  );
}

function addTokenAlternatives(into: Set<string>, value: string): void {
  const normalized = normalizeAssetKeywordQuery(value);
  if (normalized.length >= 2) {
    into.add(normalized);
  }
  for (const token of tokenizeSearchText(normalized)) {
    if (token.length >= 2) {
      into.add(token);
    }
  }
}

let typeAlternativeMap: Map<string, string[]> | null = null;
let cityAlternativeMap: Map<string, string[]> | null = null;
let cityBigramSet: Set<string> | null = null;

function typeAlternativesByToken(): Map<string, string[]> {
  if (typeAlternativeMap) {
    return typeAlternativeMap;
  }
  const map = new Map<string, string[]>();
  for (const typeName of BROWSE_FILTER_ASSET_TYPE_NAMES) {
    const group = new Set<string>();
    for (const phrase of browseSpokenPhrasesForType(typeName)) {
      addTokenAlternatives(group, phrase);
    }
    const list = Array.from(group);
    for (const token of list) {
      const current = map.get(token) ?? [];
      map.set(token, Array.from(new Set([...current, ...list])));
    }
  }
  typeAlternativeMap = map;
  return map;
}

function cityAlternativesByToken(): Map<string, string[]> {
  if (cityAlternativeMap) {
    return cityAlternativeMap;
  }
  const map = new Map<string, string[]>();
  for (const settlement of allIsraelSettlements()) {
    if (settlement.he.includes('שבט')) {
      continue;
    }
    const group = new Set<string>();
    addTokenAlternatives(group, settlement.he);
    addTokenAlternatives(group, settlement.en);
    const heHead = settlement.he.split(/\s*[-–]\s*/)[0] ?? '';
    addTokenAlternatives(group, heHead);
    const list = Array.from(group).filter((token) => token.length >= 3);
    if (list.length === 0) {
      continue;
    }
    for (const token of list) {
      if (isLatinToken(token) && token.length < 4) {
        continue;
      }
      const current = map.get(token) ?? [];
      map.set(token, Array.from(new Set([...current, ...list])));
    }
  }
  cityAlternativeMap = map;
  return map;
}

function cityTokenBigrams(): Set<string> {
  if (cityBigramSet) {
    return cityBigramSet;
  }
  const set = new Set<string>();
  const addPhrase = (value: string) => {
    const tokens = tokenizeSearchText(normalizeAssetKeywordQuery(value)).filter(
      (token) => !QUERY_STOP_WORDS.has(token),
    );
    for (let i = 0; i < tokens.length - 1; i += 1) {
      const left = tokens[i];
      const right = tokens[i + 1];
      if (left && right) {
        set.add(`${left} ${right}`);
      }
    }
  };
  for (const settlement of allIsraelSettlements()) {
    if (settlement.he.includes('שבט')) {
      continue;
    }
    addPhrase(settlement.he);
    addPhrase(settlement.en);
    addPhrase(settlement.he.split(/\s*[-–]\s*/)[0] ?? '');
  }
  cityBigramSet = set;
  return set;
}

function alternativesForQueryToken(token: string): string[] {
  const normalized = normalizeAssetKeywordQuery(token);
  const out = new Set<string>([normalized]);
  if (/^ה[\u0590-\u05ff]{2,}$/.test(normalized)) {
    out.add(normalized.slice(1));
  } else if (/^[\u0590-\u05ff]{3,}$/.test(normalized) && !normalized.startsWith('ה')) {
    out.add(`ה${normalized}`);
  }
  hebrewLetterNameLatin(normalized).forEach((value) => out.add(value));
  if (isHebrewToken(normalized)) {
    const latin = latinizeHebrewToken(normalized);
    if (latin.length >= 2) {
      out.add(latin);
    }
  }
  const typeAlts = typeAlternativesByToken().get(normalized);
  if (typeAlts) {
    typeAlts.forEach((value) => out.add(value));
  }
  for (const stem of pluralTypeStems(normalized)) {
    out.add(stem);
    const stemAlts = typeAlternativesByToken().get(stem);
    if (stemAlts) {
      stemAlts.forEach((value) => out.add(value));
    }
  }
  const cityAlts = cityAlternativesByToken().get(normalized);
  if (cityAlts) {
    cityAlts.forEach((value) => out.add(value));
  }
  return Array.from(out);
}

/** משרדים → משרד, כיתות → כיתה, offices → office, gyms → gym. */
function pluralTypeStems(token: string): string[] {
  const stems: string[] = [];
  if (/[\u0590-\u05ff]{3,}ים$/.test(token)) {
    stems.push(token.slice(0, -2));
  }
  if (/[\u0590-\u05ff]{2,}ות$/.test(token) && token.length >= 4) {
    const withoutUt = token.slice(0, -2);
    stems.push(withoutUt);
    stems.push(`${withoutUt}ה`);
    if (token.endsWith('אות') && token.length >= 5) {
      stems.push(`${token.slice(0, -3)}ה`);
    }
  }
  if (/^[\u0590-\u05ff]{3,}י$/.test(token)) {
    stems.push(token.slice(0, -1));
  }
  if (/^[a-z]{3,}s$/i.test(token)) {
    stems.push(token.slice(0, -1));
    if (/ies$/i.test(token) && token.length >= 5) {
      stems.push(`${token.slice(0, -3)}y`);
    } else if (/es$/i.test(token) && token.length >= 5) {
      stems.push(token.slice(0, -2));
    }
  }
  return stems;
}

export type ParsedAssetKeywordQuery = {
  raw: string;
  /** AND groups; any alternative in a group may match (OR). */
  groups: string[][];
};

export function parseAssetKeywordQuery(query: string): ParsedAssetKeywordQuery {
  const raw = normalizeAssetKeywordQuery(query);
  const tokens = stripHebrewCityPrefixTokens(
    tokenizeSearchText(raw).filter((token) => !QUERY_STOP_WORDS.has(token)),
  );
  const source = tokens.length > 0 ? tokens : raw ? [raw] : [];
  return {
    raw,
    groups: source.map((token) => alternativesForQueryToken(token)),
  };
}

/**
 * True when `query` matches `text` as a substring/word prefix, or (Latin only) a 1-edit token typo.
 * Does not slide a fuzzy window across the whole string — that treated Holon as parking/Rehovot.
 */
export function fuzzyTextMatchesQuery(text: string, query: string): boolean {
  const haystack = text.toLowerCase();
  const q = normalizeAssetKeywordQuery(query);
  if (!q) {
    return false;
  }
  if (haystack.includes(q)) {
    return true;
  }
  if (!isLatinToken(q) || q.length < 3) {
    return false;
  }
  for (const token of tokenizeSearchText(haystack)) {
    if (token.startsWith(q)) {
      return true;
    }
    if (Math.abs(token.length - q.length) > 1) {
      continue;
    }
    if (levenshtein(q, token) <= 1) {
      return true;
    }
  }
  return false;
}

function addSearchField(fields: string[], raw: unknown): void {
  if (typeof raw === 'string' && raw.trim()) {
    fields.push(raw.trim().toLowerCase());
  }
}

function collectNestedSearchFields(
  fields: string[],
  record: Record<string, unknown> | null | undefined,
): void {
  if (!record) {
    return;
  }
  addSearchField(fields, record.street);
  addSearchField(fields, record.city);
  addSearchField(fields, record.line1);
  addSearchField(fields, record.line2);
  addSearchField(fields, record.formatted);
  addSearchField(fields, record.state_name);
  addSearchField(fields, record.stateName);
  addSearchField(fields, record.district_name);
  addSearchField(fields, record.districtName);
  addSearchField(fields, record.region_name);
  addSearchField(fields, record.regionName);
  addSearchField(fields, record.country_name);
  addSearchField(fields, record.countryName);
  addSearchField(fields, record.asset_type_name);
  addSearchField(fields, record.type_name);
}

/** Searchable text blobs for an asset (name, subtitle, address, description, type, region). */
export function assetRecordSearchFields(
  asset: Record<string, unknown>,
  language: AppLanguage,
): string[] {
  const fields: string[] = [];
  addSearchField(fields, assetRecordListTitle(asset));
  addSearchField(fields, asset.asset_name);
  addSearchField(fields, asset.facility_name);
  addSearchField(fields, asset.name);
  addSearchField(fields, asset.sub_title);
  addSearchField(fields, asset.description);
  addSearchField(fields, asset.asset_type_name);
  addSearchField(fields, asset.type_name);
  addSearchField(fields, asset.type);
  addSearchField(fields, asset.state_name);
  addSearchField(fields, asset.district_name);
  addSearchField(fields, asset.region_name);
  addSearchField(fields, asset.city);
  addSearchField(fields, asset.street);
  addSearchField(fields, asset.address);

  const addressLine = assetRecordListAddressLine(asset, language);
  if (addressLine) {
    addSearchField(fields, addressLine);
  }

  if (isPlainObject(asset.address)) {
    collectNestedSearchFields(fields, asset.address);
  }
  for (const key of ['facility', 'Facility', 'asset'] as const) {
    const nested = asset[key];
    if (isPlainObject(nested)) {
      collectNestedSearchFields(fields, nested);
    }
  }
  if (isPlainObject(asset.asset_type)) {
    addSearchField(fields, asset.asset_type.name);
    addSearchField(fields, asset.asset_type.label);
    addSearchField(fields, asset.asset_type.type_name);
  }

  // Amenity / facility labels (“מטבח”, “Wi‑Fi”) so “עם מטבח” can refine the catalog.
  const facilityRows: unknown[] = [];
  const pushFacilities = (raw: unknown) => {
    if (raw == null) {
      return;
    }
    if (Array.isArray(raw)) {
      facilityRows.push(...raw);
      return;
    }
    facilityRows.push(raw);
  };
  pushFacilities(asset.facilities);
  pushFacilities(asset.facility_ids);
  pushFacilities(asset.facilityIds);
  if (isPlainObject(asset.facility)) {
    pushFacilities(asset.facility.facilities);
    pushFacilities(asset.facility.facility_ids);
  }
  if (isPlainObject(asset.asset)) {
    pushFacilities(asset.asset.facilities);
    pushFacilities(asset.asset.facility_ids);
  }
  const mergedFacilities = mergeFacilitiesAndPromotedAddonsForDisplay(
    facilityRows,
    Array.isArray(asset.addons) ? asset.addons : undefined,
  );
  for (const row of mergedFacilities) {
    const name = catalogRowDisplayName(row);
    addSearchField(fields, name);
    const iconId = facilityAddonIconId(name);
    if (iconId) {
      for (const alias of FACILITY_ICON_SEARCH_ALIASES[iconId] ?? []) {
        addSearchField(fields, alias);
      }
    }
  }

  return Array.from(new Set(fields));
}

function foldHebrewArticleToken(token: string): string {
  if (/^ה[\u0590-\u05ff]{2,}$/.test(token)) {
    return token.slice(1);
  }
  return token;
}

function keywordTokensEqual(a: string, b: string): boolean {
  if (a === b) {
    return true;
  }
  if (foldHebrewArticleToken(a) === foldHebrewArticleToken(b)) {
    return true;
  }
  const aLetters = hebrewLetterNameLatin(a);
  const bLetters = hebrewLetterNameLatin(b);
  if (aLetters.includes(b) || bLetters.includes(a)) {
    return true;
  }
  return skeletonsMatch(a, b);
}

/** Longest run of query tokens that appear in order in a field (hyphens already normalized). */
function consecutiveQueryTokenCoverage(queryTokens: string[], fieldTokens: string[]): number {
  if (queryTokens.length === 0 || fieldTokens.length === 0) {
    return 0;
  }
  let best = 0;
  for (let q = 0; q < queryTokens.length; q += 1) {
    for (let f = 0; f < fieldTokens.length; f += 1) {
      let n = 0;
      while (
        q + n < queryTokens.length &&
        f + n < fieldTokens.length &&
        keywordTokensEqual(queryTokens[q + n] ?? '', fieldTokens[f + n] ?? '')
      ) {
        n += 1;
      }
      if (n > best) {
        best = n;
      }
    }
  }
  const gluedQuery = queryTokens.join('');
  if (gluedQuery.length >= 3) {
    for (const token of fieldTokens) {
      if (keywordTokensEqual(token, gluedQuery) || foldHebrewArticleToken(token) === gluedQuery) {
        best = Math.max(best, queryTokens.length);
      }
    }
  }
  return best;
}

function scoreFieldMatch(field: string, query: string, isTitle = false): AssetKeywordMatchKind {
  const haystack = normalizeAssetKeywordQuery(field);
  const needle = normalizeAssetKeywordQuery(query);
  if (!needle) {
    return 'none';
  }
  if (isTitle && haystack.startsWith(needle)) {
    return 'titlePrefix';
  }
  if (haystack.includes(needle)) {
    return isTitle ? 'titlePartial' : 'partial';
  }
  const fieldTokens = tokenizeSearchText(haystack);
  const needleTokens = tokenizeSearchText(needle);
  // Prefer strong tokens (“rabbit”); ignore weak leftovers (“hole”→l) when at
  // least one strong token matched — keeps EN brands matching HE titles.
  const requiredNeedleTokens = (() => {
    const strong = needleTokens.filter((token) => !isWeakSkeletonToken(token));
    return strong.length > 0 ? strong : needleTokens;
  })();
  if (
    requiredNeedleTokens.length > 0 &&
    requiredNeedleTokens.every((queryToken) =>
      fieldTokens.some((fieldToken) => keywordTokensEqual(queryToken, fieldToken)),
    )
  ) {
    return isTitle ? 'titlePartial' : 'partial';
  }
  if (fuzzyTextMatchesQuery(haystack, needle)) {
    return 'fuzzy';
  }
  return 'none';
}

function rankForKind(kind: AssetKeywordMatchKind): number {
  switch (kind) {
    case 'titlePrefix':
      return 400;
    case 'titlePartial':
      return 300;
    case 'partial':
      return 200;
    case 'fuzzy':
      return 100;
    default:
      return 0;
  }
}

function weakerKind(a: AssetKeywordMatchKind, b: AssetKeywordMatchKind): AssetKeywordMatchKind {
  return rankForKind(a) <= rankForKind(b) ? a : b;
}

function scoreAlternativesAgainstAsset(
  asset: Record<string, unknown>,
  alternatives: string[],
  language: AppLanguage,
): AssetKeywordMatchScore {
  let best: AssetKeywordMatchScore = { matches: false, kind: 'none', rank: 0 };
  const title = assetRecordDisplayTitle(asset).toLowerCase();
  for (const alt of alternatives) {
    const titleKind = scoreFieldMatch(title, alt, true);
    if (titleKind !== 'none') {
      const rank = rankForKind(titleKind) + (titleKind === 'fuzzy' ? 10 : 0);
      if (rank > best.rank) {
        best = { matches: true, kind: titleKind, rank };
      }
      if (titleKind === 'titlePrefix') {
        return best;
      }
    }
    for (const field of assetRecordSearchFields(asset, language)) {
      if (field === title) {
        continue;
      }
      const kind = scoreFieldMatch(field, alt, false);
      if (kind === 'none') {
        continue;
      }
      const rank = rankForKind(kind);
      if (rank > best.rank) {
        best = { matches: true, kind, rank };
      }
      if (kind === 'partial' && best.kind !== 'titlePrefix' && best.kind !== 'titlePartial') {
        break;
      }
    }
  }
  return best;
}

export function scoreAssetKeywordMatch(
  asset: Record<string, unknown>,
  query: string,
  language: AppLanguage,
): AssetKeywordMatchScore {
  return scoreParsedAssetKeywordMatch(asset, parseAssetKeywordQuery(query), language);
}

export function scoreParsedAssetKeywordMatch(
  asset: Record<string, unknown>,
  parsed: ParsedAssetKeywordQuery,
  language: AppLanguage,
): AssetKeywordMatchScore {
  if (!parsed.raw) {
    return { matches: true, kind: 'partial', rank: 0 };
  }
  if (parsed.raw.length < ASSET_KEYWORD_SEARCH_MIN_LENGTH) {
    return { matches: true, kind: 'partial', rank: 0 };
  }
  if (parsed.groups.length === 0) {
    return { matches: false, kind: 'none', rank: 0 };
  }

  // Drop AND-groups that are only weak skeletons (“hole”→l) when a stronger
  // brand token remains (“rabbit”) — otherwise EN multi-word brands never match HE titles.
  const groupsToScore = (() => {
    const strong = parsed.groups.filter(
      (group) => !group.every((alt) => isWeakSkeletonToken(alt)),
    );
    return strong.length > 0 ? strong : parsed.groups;
  })();
  const parts = groupsToScore.map((group) => scoreAlternativesAgainstAsset(asset, group, language));
  if (parts.every((part) => part.matches)) {
    const kind = parts.reduce(
      (weakest, part) => weakerKind(weakest, part.kind),
      parts[0]?.kind ?? 'partial',
    );
    const rank = Math.round(parts.reduce((sum, part) => sum + part.rank, 0) / parts.length);
    return { matches: true, kind, rank };
  }

  const queryTokens = parsed.groups.map((group) => group[0]).filter((token): token is string => Boolean(token));
  const fields = assetRecordSearchFields(asset, language).map((field) => normalizeAssetKeywordQuery(field));
  const normalizedQuery = normalizeAssetKeywordQuery(parsed.raw);
  for (const field of fields) {
    if (normalizedQuery.length >= 3 && field.includes(normalizedQuery)) {
      return { matches: true, kind: 'partial', rank: 180 };
    }
    const glued = queryTokens.join('');
    if (glued.length >= 3 && (field.includes(glued) || field.replace(/\s+/g, '').includes(glued))) {
      return { matches: true, kind: 'partial', rank: 160 };
    }
  }

  if (queryTokens.length >= 2) {
    let bestCoverage = 0;
    for (const field of fields) {
      bestCoverage = Math.max(
        bestCoverage,
        consecutiveQueryTokenCoverage(queryTokens, tokenizeSearchText(field)),
      );
    }
    if (bestCoverage >= 2) {
      const rank = Math.round(70 + (bestCoverage / queryTokens.length) * 80);
      return { matches: true, kind: 'fuzzy', rank };
    }
  }

  return { matches: false, kind: 'none', rank: 0 };
}

export function assetRecordMatchesKeyword(
  asset: Record<string, unknown>,
  query: string,
  language: AppLanguage,
): boolean {
  return scoreAssetKeywordMatch(asset, query, language).matches;
}

const CITY_ONLY_FILLER_WORDS = new Set(['in', 'at', 'near', 'city', 'עיר', 'העיר']);

/**
 * Hebrew city name when the whole query is a city ("חולון", "בקרית אונו", "in Tel Aviv").
 * Those searches filter on the listing city, not on descriptions, districts or streets.
 */
export function cityOnlyKeywordQuery(query: string): string | null {
  const match = matchVoiceSearchCity(query);
  if (!match) {
    return null;
  }
  const rest = stripHebrewPrefixLetters(removeVoicePhrase(query, match.matchedPhrase))
    .split(' ')
    .filter((token) => token && !CITY_ONLY_FILLER_WORDS.has(token.toLowerCase()));
  return rest.length === 0 ? match.cityHe : null;
}

export function filterAssetsByKeyword(
  assets: Record<string, unknown>[],
  query: string,
  language: AppLanguage,
): Record<string, unknown>[] {
  const parsed = parseAssetKeywordQuery(query);
  if (!parsed.raw || parsed.raw.length < ASSET_KEYWORD_SEARCH_MIN_LENGTH) {
    return assets;
  }
  const city = cityOnlyKeywordQuery(query);
  if (city) {
    return assets.filter((asset) => assetRecordMatchesBrowseCity(asset, city));
  }
  return assets.filter((asset) => scoreParsedAssetKeywordMatch(asset, parsed, language).matches);
}

export function rankAssetsByKeyword(
  assets: Record<string, unknown>[],
  query: string,
  language: AppLanguage,
): Record<string, unknown>[] {
  const parsed = parseAssetKeywordQuery(query);
  if (!parsed.raw || parsed.raw.length < ASSET_KEYWORD_SEARCH_MIN_LENGTH) {
    return assets;
  }
  return [...assets].sort((a, b) => {
    const scoreB = scoreParsedAssetKeywordMatch(b, parsed, language).rank;
    const scoreA = scoreParsedAssetKeywordMatch(a, parsed, language).rank;
    if (scoreB !== scoreA) {
      return scoreB - scoreA;
    }
    return assetRecordDisplayTitle(a).localeCompare(assetRecordDisplayTitle(b), language);
  });
}

/** Client refinement after catalog fetch — AND tokens, hyphen/space fold, consecutive partial. */
export function refineAssetKeywordSearchResults(
  assets: Record<string, unknown>[],
  query: string,
  language: AppLanguage,
): Record<string, unknown>[] {
  return rankAssetsByKeyword(filterAssetsByKeyword(assets, query, language), query, language);
}
